import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotificationById,
} from '../api/notificationsAPI';
import SSEToastContainer from '../components/ui/SSEToast';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // SSE toast state for advisor-side real-time notifications
    const [sseToasts, setSseToasts] = useState([]);

    // Registry of callbacks to invoke when a DOCUMENT_PROCESSED notification arrives.
    // Keyed by a caller-supplied id so components can unregister on unmount.
    const docProcessedListeners = useRef({});

    const onDocumentProcessed = useCallback((id, callback) => {
        docProcessedListeners.current[id] = callback;
        return () => { delete docProcessedListeners.current[id]; };
    }, []);

    // Track which notification ids we have already fired callbacks for so we
    // don't re-fire on subsequent polls.
    const firedNotifIds = useRef(new Set());

    const isClientPortal = location.pathname.startsWith('/household/');

    const fetchNotifications = useCallback(async () => {
        try {
            // Client-portal routes (/household/...) use their own auth and don't
            // have an advisor token — skip to avoid spurious 401s.
            if (location.pathname.startsWith('/household/')) {
                return;
            }

            // Only fetch if a token exists in localStorage
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");
            if (!userData.access_token) return;

            // Fetch notifications (default limit 50)
            const rawData = await getNotifications(50);
            const items = rawData.items && Array.isArray(rawData.items) ? rawData.items : (Array.isArray(rawData) ? rawData : []);

            setNotifications(items);

            // update unread count - use backend provided count or calculate
            const count = typeof rawData.total_unread === 'number' ? rawData.total_unread : items.filter(n => !n.is_read).length;
            setUnreadCount(count);

            // Fire DOCUMENT_PROCESSED callbacks for newly seen notifications.
            // Important: collect all new ids first, then invoke each listener once per poll.
            // Otherwise the first fetch after load can contain many historical DOCUMENT_PROCESSED
            // items — firing the listener once per item caused a burst of household refetches.
            const listeners = Object.values(docProcessedListeners.current);
            if (listeners.length > 0) {
                const newlyProcessed = [];
                items.forEach((notif) => {
                    if (notif.notification_type !== 'DOCUMENT_PROCESSED') return;
                    const idKey =
                        notif.id != null ? String(notif.id) : null;
                    if (!idKey || firedNotifIds.current.has(idKey)) return;
                    firedNotifIds.current.add(idKey);
                    newlyProcessed.push(notif);
                });
                if (newlyProcessed.length > 0) {
                    listeners.forEach((cb) => {
                        try {
                            cb(newlyProcessed);
                        } catch (e) {
                            console.error(e);
                        }
                    });
                }
            }

        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        }
    }, [location.pathname]);

    const markAsRead = async (id) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, is_read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));

            await markNotificationAsRead(id);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
            // Revert on error could be implemented here, but low priority for notifications
            fetchNotifications(); // Sync with server
        }
    };

    const markAllAsRead = async () => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);

            await markAllNotificationsAsRead();
        } catch (error) {
            console.error("Failed to mark all as read:", error);
            fetchNotifications();
        }
    };

    const deleteNotification = async (id) => {
        try {
            // Optimistic
            const notif = notifications.find(n => n.id === id);
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (notif && !notif.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            await deleteNotificationById(id);
        } catch (error) {
            console.error("Failed to delete notification:", error);
            fetchNotifications();
        }
    };

    // Initial fetch and polling
    useEffect(() => {
        fetchNotifications();

        const intervalId = setInterval(() => {
            fetchNotifications();
        }, 45000); // Poll every 45 seconds

        return () => clearInterval(intervalId);
    }, [fetchNotifications]);

    // ── Advisor SSE Connection ──────────────────────────────────────────────
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptRef = useRef(0);

    const cleanupSSE = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    const fireDocProcessedListeners = useCallback((event) => {
        const listeners = Object.values(docProcessedListeners.current);
        if (listeners.length > 0) {
            // Mark as already fired so polling doesn't re-fire
            if (event.id) firedNotifIds.current.add(String(event.id));
            listeners.forEach((cb) => {
                try { cb([event]); } catch (e) { console.error(e); }
            });
        }
    }, []);

    const handleSSEEvent = useCallback((rawEvent) => {
        try {
            const event = JSON.parse(rawEvent.data);
            console.log('SSE event', event.notification_type, event);

            // Show toast for document processing events
            if (['DOCUMENT_PROCESSED', 'TRANSCRIPT_PROCESSED', 'AUDIO_PROCESSED'].includes(event.notification_type)) {
                const isSuccess = event.document_status === 'completed';
                setSseToasts((prev) => [
                    ...prev,
                    {
                        id: event.id || `toast-${Date.now()}`,
                        title: isSuccess
                            ? event.title || 'Document processed successfully'
                            : event.title || 'Document processing failed',
                        fileName: event.file_name || 'Unknown document',
                        status: event.document_status || (isSuccess ? 'completed' : 'failed'),
                        duration: 8000,
                    },
                ]);

                // Notify listeners (e.g. HouseholdContent) so they can refresh data
                fireDocProcessedListeners(event);
            }

            // Show toast for household creation events
            if (['HOUSEHOLD_CREATED', 'BULK_HOUSEHOLD_CREATION_FAILED'].includes(event.notification_type)) {
                const isSuccess = event.notification_type === 'HOUSEHOLD_CREATED';
                setSseToasts((prev) => [
                    ...prev,
                    {
                        id: event.id || `toast-${Date.now()}`,
                        title: event.title || (isSuccess ? 'Household Created' : 'Household Creation Failed'),
                        fileName: event.message || '',
                        status: isSuccess ? 'completed' : 'failed',
                        duration: 8000,
                    },
                ]);

                // Fire on success (no household_id on bulk events, so don't gate on it).
                if (isSuccess) {
                    fireDocProcessedListeners(event);
                }
            }

            // Refresh notification list to keep bell icon in sync
            fetchNotifications();
        } catch (err) {
            // Ignore unparseable events (e.g. heartbeat comments)
        }
    }, [fetchNotifications, fireDocProcessedListeners]);

    const connectSSE = useCallback(() => {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const token = userData.access_token;
        if (!token) return;

        cleanupSSE();

        const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
        const sseUrl = `${baseUrl}/notifications/sse/stream?token=${encodeURIComponent(token)}`;

        try {
            const es = new EventSource(sseUrl);
            eventSourceRef.current = es;

            es.onopen = () => {
                reconnectAttemptRef.current = 0;
            };

            es.onmessage = handleSSEEvent;
            es.addEventListener("notification", handleSSEEvent);
            es.addEventListener("document_processed", handleSSEEvent);

            es.onerror = () => {
                es.close();
                eventSourceRef.current = null;

                // Reconnect with exponential backoff (max 30s)
                const delay = Math.min(
                    1000 * Math.pow(2, reconnectAttemptRef.current),
                    30000,
                );
                reconnectAttemptRef.current += 1;

                reconnectTimeoutRef.current = setTimeout(() => {
                    connectSSE();
                }, delay);
            };
        } catch (err) {
            console.error("Failed to establish advisor SSE connection:", err);
        }
    }, [cleanupSSE, handleSSEEvent]);

    // Connect SSE for advisor pages only (not client portal)
    useEffect(() => {
        if (isClientPortal) {
            cleanupSSE();
            return;
        }

        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        if (userData.access_token) {
            connectSSE();
        }

        return cleanupSSE;
    }, [isClientPortal, connectSSE, cleanupSSE]);

    const dismissToast = useCallback((id) => {
        setSseToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            deleteNotification,
            refreshNotifications: fetchNotifications,
            onDocumentProcessed,
        }}>
            {children}
            {/* Advisor SSE toasts — only render outside client portal */}
            {!isClientPortal && (
                <SSEToastContainer toasts={sseToasts} onDismiss={dismissToast} />
            )}
        </NotificationContext.Provider>
    );
};
